import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  TypographyVariant,
} from '@mui/material';

import { Hint, TextField } from '@components/base';
import InputGrid from '@components/base/layout/InputGrid';
import { TOrderProvidersBy } from '@models/OrderProvidersBy';
import { ServiceType, ServiceTypes } from '@models/Provider';
import ProviderSelector from '@modules/layouts/components/ProviderSelector';

export type OrderProvidersByProps = {
  fieldWrapperProps?: StackProps;
  headingProps?: TypographyProps;
  helperText?: string;
  providerIdentifier?: TOrderProvidersBy['id'];
  serviceType?: TOrderProvidersBy['type'];
  setProviderIdentifier: (providerIdentifier?: string) => void;
  setServiceType: (serviceType?: ServiceType) => void;
};

export const OrderProvidersBy: FC<OrderProvidersByProps> = ({
  headingProps: { variant: headingVariant = 'h3', ...headingProps } = {},
  helperText,
  providerIdentifier,
  serviceType,
  setProviderIdentifier,
  setServiceType,
  fieldWrapperProps,
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1.25}>
      <Stack alignItems='center' direction='row' spacing={1}>
        <Typography variant={headingVariant as TypographyVariant} {...headingProps}>
          {t('providerOrdering')}
        </Typography>

        {helperText && <Hint icon='info' tooltip={helperText} />}
      </Stack>

      <InputGrid {...fieldWrapperProps}>
        <ProviderSelector
          includeChannels
          label={t('providerOrdering.provider')}
          onChange={setProviderIdentifier}
          value={providerIdentifier}
        />

        <Autocomplete
          onChange={(_, value) => setServiceType(value as ServiceType)}
          options={ServiceTypes}
          renderInput={params => (
            <TextField {...params} label={t('providerOrdering.serviceType')} />
          )}
          value={serviceType || null}
        />
      </InputGrid>
    </Stack>
  );
};

export default OrderProvidersBy;
